import { useState } from 'react';
import { CalendarIcon, ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { monthLabel, parseMonthInput } from '@/lib/credit-contracts/month';
import { trimWhiteSpace } from '@/lib/credit-conversion-settings/number';
import { cn } from '@/lib/utils';

export interface MonthGridProps {
  /** 選んでいる月（`YYYY-MM`）。なければ null */
  value: string | null;
  /** 当月（`YYYY-MM`）。印を付け、選んでいる月がなければ当月の年から始める */
  current: string;
  /** これより前の月は選べない（`YYYY-MM`） */
  min?: string;
  /** これより後の月は選べない（`YYYY-MM`） */
  max?: string;
  onSelect: (month: string) => void;
}

const pad = (n: number, width: number) => String(n).padStart(width, '0');

/** 年を前後に送り、12か月から1つを選ぶ */
export function MonthGrid({ value, current, min, max, onSelect }: MonthGridProps) {
  const [year, setYear] = useState(() => parseInt((value ?? current).slice(0, 4), 10));
  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center justify-between">
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-8 w-8"
          aria-label="前の年"
          disabled={year <= 1}
          onClick={() => setYear((y) => y - 1)}
        >
          <ChevronLeftIcon />
        </Button>
        <span className="text-sm font-medium" aria-live="polite">
          {year}年
        </span>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-8 w-8"
          aria-label="次の年"
          disabled={year >= 9999}
          onClick={() => setYear((y) => y + 1)}
        >
          <ChevronRightIcon />
        </Button>
      </div>
      <div className="grid grid-cols-4 gap-1">
        {Array.from({ length: 12 }, (_, i) => {
          const month = `${pad(year, 4)}-${pad(i + 1, 2)}`;
          const selected = month === value;
          return (
            <Button
              key={month}
              type="button"
              size="sm"
              variant={selected ? 'default' : 'ghost'}
              aria-label={`${year}年${i + 1}月`}
              aria-pressed={selected}
              disabled={(min !== undefined && month < min) || (max !== undefined && month > max)}
              className={cn(
                'h-9 px-0',
                month === current && !selected && 'ring-1 ring-inset ring-primary',
              )}
              onClick={() => onSelect(month)}
            >
              {i + 1}月
            </Button>
          );
        })}
      </div>
    </div>
  );
}

export interface MonthFieldProps {
  id: string;
  label: string;
  /** 入力欄の文字列（`YYYY/MM`） */
  value: string;
  onChange: (value: string) => void;
  current: string;
  min?: string;
  /** 空欄にできる月（終了月）。小窓に「空欄にする（継続中）」を置く */
  clearable?: boolean;
  disabled?: boolean;
  error?: string;
  hint?: string;
}

/** 月の入力欄。`YYYY/MM` を手で打つか、ボタンで開く小窓から選ぶ */
export function MonthField({
  id,
  label,
  value,
  onChange,
  current,
  min,
  clearable,
  disabled,
  error,
  hint,
}: MonthFieldProps) {
  const [open, setOpen] = useState(false);
  const choose = (next: string) => {
    onChange(next);
    setOpen(false);
  };
  return (
    <div className="flex flex-col gap-1.5">
      <Label htmlFor={id}>{label}</Label>
      <div className="flex gap-1.5">
        <Input
          id={id}
          value={value}
          placeholder="YYYY/MM"
          inputMode="numeric"
          disabled={disabled}
          aria-invalid={error ? true : undefined}
          aria-describedby={error ? `${id}-error` : undefined}
          className={error ? 'border-red-500' : undefined}
          onChange={(e) => onChange(e.target.value)}
        />
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button
              type="button"
              variant="outline"
              size="icon"
              className="shrink-0"
              aria-label={`${label}を選ぶ`}
              disabled={disabled}
            >
              <CalendarIcon />
            </Button>
          </PopoverTrigger>
          <PopoverContent align="end" className="w-64 p-3">
            <MonthGrid
              value={parseMonthInput(trimWhiteSpace(value))}
              current={current}
              min={min}
              onSelect={(month) => choose(monthLabel(month))}
            />
            {clearable && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="mt-2 w-full"
                onClick={() => choose('')}
              >
                空欄にする（継続中）
              </Button>
            )}
          </PopoverContent>
        </Popover>
      </div>
      {hint && !error && <p className="text-xs text-gray-500">{hint}</p>}
      {error && (
        <p id={`${id}-error`} className="text-xs text-red-600" data-error="true">
          {error}
        </p>
      )}
    </div>
  );
}
